/** @jsx jsx */
import React, { type ReactNode } from 'react';
import { css, jsx } from '@compiled/react';
import Button, { type ButtonProps } from '@atlaskit/button';
import { ARTICLE_TYPE } from '@atlaskit/help';
import { isIE11 } from '@atlassian/jira-common-util-browser';
import { ff } from '@atlassian/jira-feature-flagging';
import { fg } from '@atlassian/jira-feature-gating';
import { HELP_PANEL_ID, HELP_PANEL_WIDTH } from '@atlassian/jira-help-panel-common-constant';
import { useHelpPanelActions } from '@atlassian/jira-help-panel-services';
import { useRightSidebarController } from '@atlassian/jira-layout-controller';

export interface HelpPanelButtonProps extends Omit<ButtonProps, 'type'> {
	articleId?: string;
	type?: keyof typeof ARTICLE_TYPE;
	children?: ReactNode;
	fallbackComponent: ReactNode;
}

export const useHelpPanelLink = (
	resetHistory = false,
): ((id: string, type?: keyof typeof ARTICLE_TYPE) => void) | undefined => {
	const { openRightSidebar } = useRightSidebarController();
	const [, { setNavigationData }] = useHelpPanelActions();

	const isIphEnabled = ff('iph.enabled') && !isIE11();

	const handleOnClick = (id: string, type: keyof typeof ARTICLE_TYPE = 'HELP_ARTICLE') => {
		openRightSidebar(HELP_PANEL_ID, HELP_PANEL_WIDTH, false, true);

		if (resetHistory) {
			setNavigationData({
				articleId: {
					id,
					type: ARTICLE_TYPE[type],
				},
				history: [],
			});
		} else {
			setNavigationData({
				articleId: {
					id,
					type: ARTICLE_TYPE[type],
				},
			});
		}
	};
	return isIphEnabled ? handleOnClick : undefined;
};

const HelpPanelLink = ({
	articleId = '',
	type = 'HELP_ARTICLE',
	children,
	fallbackComponent = null,
	onClick,
	...props
}: HelpPanelButtonProps) => {
	const onOpenInProductHelpArticle = useHelpPanelLink();

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const handleOnClick = (event: any, analyticsEvent: any) => {
		onClick?.(event, analyticsEvent);
		onOpenInProductHelpArticle?.(articleId, ARTICLE_TYPE[type]);
	};

	return onOpenInProductHelpArticle ? (
		<Button
			{...props}
			onClick={handleOnClick}
			// eslint-disable-next-line @atlaskit/design-system/no-unsafe-style-overrides
			css={
				props.appearance === 'link' &&
				fg('a11y_add_underline_to_link_button') &&
				buttonWrapperStyles
			}
		>
			{children}
		</Button>
	) : (
		<>{fallbackComponent}</>
	);
};
export default HelpPanelLink;
const buttonWrapperStyles = css({
	textDecoration: 'underline',
	'&:hover': {
		textDecoration: 'none',
	},
});
